import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, Descriptions, Spin, message, Tabs, Tag } from "antd";
import {
  deleteClientDocument,
  getClientById,
} from "../../api/client/clientRequest";
import {
  CLIENT_TYPES,
  CLIENT_TYPES_COLORS,
  formatDateTime,
} from "../../utils/constants";
import DocumentPreview from "./DocumentPreview";
import ModifyClientInfo from "./ModifyClientInfo";
import DocumentUpload from "./DocumentUpload";
import VehicleTable from "./VehicleTable";
import { useSelector } from "react-redux";
import { hasPermission } from "../../store/userSlice";
import PermissionRender from "../../components/PermissionRender";

const { TabPane } = Tabs;

const ClientDetail = () => {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);

  const fetchClientDetails = async () => {
    setLoading(true);
    try {
      const response = await getClientById(id);
      setClient(response);
      setVehicles(response.vehicles || []);
    } catch (error) {
      message.error("Erreur lors du chargement des détails du client");
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientDetails();
  }, [id]);

  const onDeleteDocument = async (documentName) => {
    try {
      const response = await deleteClientDocument(client.id, {
        file_to_delete: documentName,
      });
      message.success("Document supprimé avec succès");
      setClient(response);
    } catch (err) {
      message.error("Erreur lors de la suppression du document");
      console.error("Erreur:", err);
    }
  };

  const onDocumentsUploaded = async (response) => {
    setClient(response);
    setVehicles(response.vehicles || []);
  };

  const handleClientUpdated = () => {
    fetchClientDetails();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" tip="Chargement..." />
      </div>
    );
  }

  if (!client) {
    return <div className="p-4 md:p-6 text-center">Client non trouvé.</div>;
  }

  const getDisplayName = (client) => {
    if (!client) return "N/A";
    if (client.type === "individuals") {
      return (
        `${client.civility || ""} ${client.first_name || ""} ${
          client.last_name || ""
        }`.trim() || "N/A"
      );
    }
    return client.name || "N/A";
  };

  const displayName = getDisplayName(client);

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <h1 className="text-2xl font-bold">Détails du Client: {displayName}</h1>
        <PermissionRender requiredPermission={["clients", "update"]}>
          <ModifyClientInfo
            client={client}
            onClientUpdated={handleClientUpdated}
          />
        </PermissionRender>
      </div>

      <Card className="shadow-md mb-6">
        <Descriptions
          bordered
          layout="vertical"
          column={{ xs: 1, sm: 2, md: 3 }}
        >
          <Descriptions.Item label="Nom Complet / Raison Sociale">
            {displayName}
          </Descriptions.Item>
          <Descriptions.Item label="Type de client">
            <Tag color={CLIENT_TYPES_COLORS[client?.type] || "default"}>
              {CLIENT_TYPES[client?.type] || "N/A"}
            </Tag>
          </Descriptions.Item>
          {client?.type === "individuals" && (
            <>
              <Descriptions.Item label="CIN">
                {client?.cin || "N/A"}
              </Descriptions.Item>
            </>
          )}
          {client?.type === "companies" && (
            <>
              <Descriptions.Item label="ICE">
                {client?.ice || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="RC">
                {client?.rc || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Prénom Contact">
                {client?.contact_first_name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Nom Contact">
                {client?.contact_last_name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Téléphone Contact">
                {client?.contact_phone || "N/A"}
              </Descriptions.Item>
            </>
          )}
          {client?.type === "institutions" && (
            <>
              {client?.public_institution ? (
                <Descriptions.Item label="Institution publique">
                  {client.public_institution.name}
                </Descriptions.Item>
              ) : (
                <Descriptions.Item label="Institution publique">
                  N/A
                </Descriptions.Item>
              )}
            </>
          )}
          <Descriptions.Item label="Email">
            {client?.email || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Téléphone Principal">
            {client?.phone || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Adresse Ligne 1">
            {client?.address || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Adresse Ligne 2">
            {client?.address2 || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Ville">
            {client?.city || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Code Postal">
            {client?.postal_code || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Pays">
            {client?.country || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Date d'ajout">
            {client?.created_at ? formatDateTime(client.created_at) : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Dernière mise à jour">
            {client?.updated_at ? formatDateTime(client.updated_at) : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Dernière mise à jour par">
            {client?.last_user_update?.full_name || "N/A"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card className="shadow-md">
        <Tabs defaultActiveKey="1">
          <TabPane tab="Véhicules" key="1">
            <VehicleTable
              vehicles={vehicles}
              onClientUpdate={fetchClientDetails}
              clientId={id}
            />
          </TabPane>
          <TabPane tab="Documents" key="2">
            <DocumentUpload
              client={client}
              onDocumentsUploaded={onDocumentsUploaded}
            />
            <DocumentPreview
              client={client}
              onDeleteDocument={onDeleteDocument}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default ClientDetail;
